import {
  type Bet,
  type BetEvent,
  type CouponIdentifiers,
  isBet,
  isBetEvent,
  isOutcomeGroup,
  type OutcomeGroup,
} from '../../../../schema/coupon/entry.js';
import type { EventNumber } from '../../../../schema/sport/entry.js';
import { createBrandedData } from '../../../../schema/type-utils/entry.js';
import { getInstanceClient } from '../../../di/entry.browser.js';
import type { ActionStatus } from '../../../lib/entry.js';

function buildPayload(
  outcomes: Bet | BetEvent | OutcomeGroup,
  eventNumber?: EventNumber,
  groupIndex?: number
): { group: number; event: EventNumber; outcomes: OutcomeGroup }[] | undefined {
  if (isOutcomeGroup(outcomes) && groupIndex !== undefined && eventNumber) {
    return [
      {
        group: groupIndex,
        event: eventNumber,
        outcomes,
      },
    ];
  }

  if (isBetEvent(outcomes) && eventNumber) {
    return Object.entries(outcomes).map(([index, outcomeGroup]) => ({
      group: Number.parseInt(index, 10),
      event: eventNumber,
      outcomes: outcomeGroup,
    }));
  }

  if (isBet(outcomes)) {
    const payload: { group: number; event: EventNumber; outcomes: OutcomeGroup }[] = [];
    for (const [eventIndex, eventOutcomes] of Object.entries(outcomes)) {
      for (const [eventGroupIndex, groupOutcomes] of Object.entries(eventOutcomes)) {
        payload.push({
          group: Number.parseInt(eventGroupIndex, 10),
          event: createBrandedData<EventNumber>(Number.parseInt(eventIndex, 10) + 1),
          outcomes: groupOutcomes,
        });
      }
    }
    return payload;
  }

  return undefined;
}

type UpdateOutcomeArgs = {
  boardIndex: number;
  eventNumber: EventNumber;
  groupIndex: number;
};

export type Intention = 'keep' | 'reset';

export async function updateCouponOutcome(
  outcomes: OutcomeGroup,
  args: Pick<UpdateOutcomeArgs, 'boardIndex' | 'eventNumber' | 'groupIndex'> & CouponIdentifiers,
  intention?: Intention
): Promise<ActionStatus>;
export async function updateCouponOutcome(
  outcomes: BetEvent,
  args: Pick<UpdateOutcomeArgs, 'boardIndex' | 'eventNumber'> & CouponIdentifiers,
  intention?: Intention
): Promise<ActionStatus>;
export async function updateCouponOutcome(
  outcomes: Bet,
  args: Pick<UpdateOutcomeArgs, 'boardIndex'> & CouponIdentifiers,
  intention?: Intention
): Promise<ActionStatus>;

/**
 * Toggles a given outcome in the coupon.
 *
 * @param outcomes Outcome to toggle
 * @param root0 function args
 * @param root0.couponId Unique id of the coupon
 * @param root0.couponIndex Specific coupon id in order
 * @param root0.eventNumber Event number in the sequence
 * @param root0.groupIndex Event group index, mainly used for result
 * @param root0.boardIndex Used for multiple board bets, like SROW
 * @param intention How the coupon should be updated
 * @returns SUCCESS if successful, otherwise ERROR
 */
export async function updateCouponOutcome(
  outcomes: Bet | BetEvent | OutcomeGroup,
  {
    couponId,
    couponIndex,
    eventNumber,
    groupIndex,
    boardIndex,
  }: Omit<Partial<UpdateOutcomeArgs>, 'boardIndex'> & {
    boardIndex: UpdateOutcomeArgs['boardIndex'];
  } & CouponIdentifiers,
  intention?: Intention
): Promise<ActionStatus> {
  const getCouponController = getInstanceClient('IGetCouponController');
  const updateCouponBoardController = getInstanceClient('IUpdateCouponBoardController');
  const [getCoupon] = getCouponController({ couponId, couponIndex });

  try {
    const coupon = getCoupon();

    if (!coupon) {
      return 'ERROR';
    }

    const payload = buildPayload(outcomes, eventNumber, groupIndex);

    if (!payload) {
      return 'ERROR';
    }

    await updateCouponBoardController(
      {
        outcomes: {
          values: payload,
          intention: intention,
        },
      },
      {
        couponId,
        couponIndex,
        boardIndex,
      }
    );

    return 'SUCCESS';
  } catch {
    return 'ERROR';
  }
}
